import { Area, AreaChart, ResponsiveContainer } from "recharts";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";

export const COLORS = {
  efi: "#34d399", openpix: "#38bdf8", amber: "#fbbf24", red: "#f87171", violet: "#a78bfa",
  rose: "#fb7185", slate: "#64748b", grid: "#1e293b", text: "#94a3b8",
};

// O 2o argumento pode vir do Recharts (objeto do ponto) quando usada direto como formatador:
// so aceita numero, senao usa 2 casas (um valor invalido derrubava a tela inteira no hover).
export const brl = (v, d) => {
  const n = typeof d === "number" && d >= 0 && d <= 6 ? d : 2;
  return `R$ ${(Number(v) || 0).toLocaleString("pt-BR", { minimumFractionDigits: n, maximumFractionDigits: n })}`;
};
export const brlCompact = (v) => {
  const n = Number(v) || 0;
  if (Math.abs(n) >= 1000) return `R$ ${(n / 1000).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}k`;
  return `R$ ${n.toLocaleString("pt-BR", { maximumFractionDigits: 0 })}`;
};
export const num = (v) => (Number(v) || 0).toLocaleString("pt-BR");
export const dm = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : "");
const WD = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];
export const weekday = (iso) => WD[new Date(`${iso}T12:00:00`).getDay()];
export const fmtHours = (h) => {
  if (h == null) return "—";
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h < 48) return `${h.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} h`;
  return `${(h / 24).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} dias`;
};

/** Cartao de indicador: o numero nunca quebra de linha - a fonte encolhe com a largura do cartao. */
export function Kpi({ label, value, sub, icon: Icon, accent = "text-emerald-400", delta, spark, sparkColor = COLORS.efi, testId, invertDelta }) {
  const good = delta == null ? null : invertDelta ? delta <= 0 : delta >= 0;
  return (
    <div
      data-testid={testId}
      className="relative overflow-hidden rounded-xl border border-slate-800 bg-gradient-to-b from-slate-900/80 to-slate-900/40 p-4 stagger-in"
      style={{ containerType: "inline-size" }}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-[0.1em] text-slate-500 truncate">{label}</div>
        {Icon && <Icon className={`w-4 h-4 shrink-0 ${accent}`} strokeWidth={1.75} />}
      </div>
      <div
        className="mt-2 font-bold text-white font-mono-jb tabular-nums whitespace-nowrap leading-none"
        style={{ fontSize: "clamp(1.05rem, 11cqw, 1.85rem)" }}
        title={typeof value === "string" ? value : undefined}
      >
        {value}
      </div>
      <div className="mt-2 flex items-center gap-2 min-h-[20px]">
        {delta != null && (
          <span className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-medium whitespace-nowrap ${good ? "bg-emerald-500/10 text-emerald-300" : "bg-red-500/10 text-red-300"}`}>
            {delta >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            {Math.abs(delta).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%
          </span>
        )}
        {sub && <span className="text-[11px] text-slate-500 truncate">{sub}</span>}
      </div>
      {spark && spark.length > 1 && (
        <div className="absolute inset-x-0 bottom-0 h-9 opacity-70 pointer-events-none">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={spark.map((v, i) => ({ i, v }))} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id={`sp-${testId}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={sparkColor} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={sparkColor} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area type="monotone" dataKey="v" stroke={sparkColor} strokeWidth={1.5} fill={`url(#sp-${testId})`} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
      {spark && spark.length > 1 && <div className="h-6" />}
    </div>
  );
}

export function ChartCard({ title, subtitle, right, children, className = "", testId }) {
  return (
    <div data-testid={testId} className={`rounded-xl border border-slate-800 bg-slate-900/50 p-5 stagger-in ${className}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div className="min-w-0">
          <div className="font-outfit text-white font-semibold leading-tight">{title}</div>
          {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

export function ChartTooltip({ active, payload, label, labelFormat, valueFormat, hideZero = true }) {
  if (!active || !payload || !payload.length) return null;
  const rows = payload.filter((p) => p.value != null && (!hideZero || p.value !== 0 || payload.length === 1));
  return (
    <div className="rounded-lg border border-slate-700 bg-slate-950/95 px-3 py-2 shadow-xl text-xs backdrop-blur">
      <div className="text-slate-300 font-medium mb-1">{labelFormat ? labelFormat(label, payload) : label}</div>
      {rows.map((p) => (
        <div key={p.dataKey + p.name} className="flex items-center justify-between gap-4 py-0.5">
          <span className="flex items-center gap-1.5 text-slate-400">
            <span className="w-2 h-2 rounded-full" style={{ background: p.color || p.fill || p.stroke }} />
            {p.name}
          </span>
          <span className="font-mono-jb text-white tabular-nums">{valueFormat ? valueFormat(p.value, p) : num(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

export function LegendChips({ items, hidden, onToggle }) {
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {items.map((it) => {
        const off = hidden?.[it.key];
        return (
          <button
            key={it.key}
            type="button"
            onClick={() => onToggle?.(it.key)}
            className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] transition ${off ? "border-slate-800 text-slate-600" : "border-slate-700 text-slate-300 hover:border-slate-500"}`}
          >
            <span className="w-2 h-2 rounded-full" style={{ background: off ? "#334155" : it.color }} />
            {it.label}
          </button>
        );
      })}
    </div>
  );
}

export function Empty({ children = "Sem dados no período" }) {
  return <div className="h-full min-h-[160px] flex items-center justify-center text-sm text-slate-600">{children}</div>;
}

export function Skeleton({ className = "" }) {
  return <div className={`animate-pulse rounded-xl bg-slate-800/40 ${className}`} />;
}
